import React, { useEffect, useMemo, useState, useRef } from "react";
import Map, { Marker, Popup, Source, Layer } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./world-mood-mobile.css";

import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp, // ⚡ додали
} from "firebase/firestore";

import { auth, db, googleProvider } from "./firebase";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";

import { WomCard, WomButton, WomSegmented } from "./design-system/components";
import AnalyticsPanel from "./AnalyticsPanel";
import UserMoodLifeMap from "./UserMoodLifeMap";


// ============================
// DESIGN TOKENS
// ============================

const tokens = {
  color: {
    bgApp: "#020617",
    textMain: "#e2e8f0",
    textSecondary: "rgba(148,163,184,0.9)",
    borderSubtle: "rgba(148,163,184,0.25)",
    accent: "#38bdf8",
  },
};

const TELEGRAM_FEEDBACK_URL = "https://t.me/+5x1apHpGRko4Zjdi";


// PayPal donate URL
const PAYPAL_DONATE_URL = "https://Ko-fi.com/wommap";

 // Monobank donate URL (замiни на свiй лiнк банки)
const MONOBANK_DONATE_URL =
  "https://send.monobank.ua/jar/5fMhk4hxQa";

  const SUBSTACK_URL = "https://wommap.substack.com";






// main CTA button (share mood) under our dark design
const PRIMARY_CTA_BUTTON = {
  width: "100%",
  borderRadius: 999,
  padding: "10px 14px",
  fontSize: 14,
  fontWeight: 600,
  border: "1px solid rgba(56,189,248,0.9)",
  background: "linear-gradient(90deg, #38bdf8, #22c55e)",
  color: "#0f172a",
  boxShadow: "0 12px 24px rgba(15,23,42,0.7)",
  cursor: "pointer",
};

const DONATE_BUTTON = {
  ...PRIMARY_CTA_BUTTON,
  marginTop: 8,
  textDecoration: "none",
  display: "flex",
  justifyContent: "center",
};

const FEEDBACK_BUTTON = {
  ...PRIMARY_CTA_BUTTON,
  marginTop: 8,
  textDecoration: "none",
  display: "flex",
  justifyContent: "center",
  // синій градієнт замість жовто-зеленого
  background: "linear-gradient(90deg, #3b82f6, #0ea5e9)",
  border: "1px solid rgba(59,130,246,0.9)",
};



// small social share buttons
const SOCIAL_BUTTON = {
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 11,
  border: `1px solid ${tokens.color.borderSubtle}`,
  background: "rgba(15,23,42,0.9)",
  color: tokens.color.textSecondary,
  cursor: "pointer",
};


// auth buttons (sign in / sign out) in dark style
const AUTH_BUTTON = {
  width: "100%",
  borderRadius: 999,
  padding: "8px 12px",
  fontSize: 13,
  fontWeight: 500,
  border: `1px solid ${tokens.color.borderSubtle}`,
  background: "rgba(15,23,42,0.9)",
  color: tokens.color.textMain,
  cursor: "pointer",
};

// ============================
// TRANSLATIONS (EN ONLY)
// ============================


const translations = {
  en: {
    worldMoodTitle: "WomMap — world mood map",
    worldMoodSubtitle: "Share how you feel and see how the planet feels.",

    // onboarding steps
    onboardingStep1Title: "Step 1 — choose your mood",
    onboardingStep1Text:
      "Tap the emoji that best matches how you feel right now.",
    onboardingStep2Title: "Step 2 — set your location",
    onboardingStep2Text:
      "Roughly mark where you are on the map or in the location field.",
    onboardingStep3Title: "Step 3 — share your mood",
    onboardingStep3Text:
      "Press “Share mood” to add your dot to the world map.",

    howDoYouFeel: "How do you feel right now?",

    whereAreYou: "Where are you now?",
    shareMood: "Share mood",
    shareThanks: "Mood saved! Thank you for sharing 💛",
    aboutTitle: "WomMap · World Mood Map",
    aboutParagraph1:"WomMap is an interactive world mood map. Anyone can log their current emotion, see how others feel, and gently track their own emotional waves over time.",
    aboutParagraph2:"Instead of long surveys, you simply choose an emoji in a few taps. Based on these check-ins, WomMap builds analytics: which moods are most common for you, how they shift across days and locations, and what makes weekends feel different from weekdays.",
    aboutParagraph3:"We don’t sell or share your personal data with third parties. Your mood history belongs to you. The goal of this project is to give you a bit more awareness and a soft invitation to ask yourself: “How do I actually feel today?”.",


    // tabs
    tabMap: "Map",
    tabAnalytics: "Analytics",
    tabMyMood: "My mood",

    // toggles
    modeEmoji: "Emoji",
    modeHeatmap: "Heatmap",
    themeDark: "Dark",
    themeLight: "Light",

    // contexts
    contextHome: "Home",
    contextWork: "Work",
    contextRestaurant: "Restaurant",
    contextFun: "Fun",
    contextShopping: "Shopping",
    contextHospital: "Hospital",

    // note
    moodNotePlaceholder: "Optionally describe your mood (max 40 chars)...",
    shareOnSocials: "Share your mood on:",

    // analytics tab
    analyticsHint:
      "Open the full-screen analytics panel over the map to explore mood distributions by country, city and time range.",
    myMoodHint:
      "Your personal mood history opens in a centered modal over the map.",
    openAnalytics: "Open analytics",

    // auth
    signIn: "Sign in with Google",
    signOut: "Sign out",
    planLabel: "Plan",

    // footer
    footerBrand: "Map of emotions · 2025",
    footerAbout: "About",
    footerPrivacy: "Privacy",
    footerFeedback: "Feedback in Telegram",
    footerSupport: "Support on Ko-fi",



    // profile modal
    profileTitle: "Your profile",
    profileName: "Name",
    profileEmail: "Email",
    profilePlan: "Plan",
    profileMoodEntries: "Mood entries",
    profileLastEntry: "Last entry",
  },

    uk: {
    worldMoodTitle: "WomMap — карта настрою світу",
    worldMoodSubtitle:
      "Поділися, як ти почуваєшся, і подивися, як почувається планета.",

    // onboarding steps
    onboardingStep1Title: "Крок 1 — обери настрій",
    onboardingStep1Text:
      "Натисни на емодзі, яке найкраще описує твій стан зараз.",
    onboardingStep2Title: "Крок 2 — вкажи місце",
    onboardingStep2Text:
      "Приблизно познач своє місце на мапі або в полі локації.",
    onboardingStep3Title: "Крок 3 — поділися настроєм",
    onboardingStep3Text:
      "Натисни «Поділитися настроєм», щоб додати себе на мапу.",

    howDoYouFeel: "Як ти почуваєшся зараз?",

    whereAreYou: "Де ти зараз?",
    shareMood: "Поділитися настроєм",
    shareThanks: "Настрій збережено! Дякую, що поділився настроєм 💛",
    aboutTitle: "Карта емоцій WomMap",
    aboutParagraph1:"WomMap — це інтерактивна карта емоцій світу. Кожен може відмітити свій настрій, побачити, як почуваються інші, і м’яко відстежувати власні емоційні хвилі в часі.",
    aboutParagraph2:"Замість великих опитувань — маленькі емодзі, які ти ставиш кількома кліками. На основі цих позначок WomMap будує аналітику: які емоції переважають у тебе, як змінюється настрій у різних точках світу, що відрізняє будні від вихідних.",
    aboutParagraph3:"Ми не продаємо й не передаємо твої дані третім сторонам. Персональні записи настрою належать тобі. Мета проєкту — дати більше усвідомленості й м’який привід поговорити з собою: «Як я насправді почуваюся?».",


    // tabs
    tabMap: "Карта",
    tabAnalytics: "Аналітика",
    tabMyMood: "Мій настрій",

    // toggles
    modeEmoji: "Емодзі",
    modeHeatmap: "Теплокарта",
    themeDark: "Темна",
    themeLight: "Світла",

    // contexts
    contextHome: "Дім",
    contextWork: "Робота",
    contextRestaurant: "Ресторан",
    contextFun: "Розваги",
    contextShopping: "Покупки",
    contextHospital: "Лікарня",

    // note
    moodNotePlaceholder:
      "За бажанням опиши свій настрій (до 40 символів)...",
    shareOnSocials: "Поділитися настроєм у соцмережах:",

    // analytics tab
    analyticsHint:
      "Розгорнута аналітика відкривається у вікні поверх карти.",
    myMoodHint:
      "Твоя особиста історія настрою відкривається у вікні поверх карти.",
    openAnalytics: "Відкрити аналітику",

    // auth
    signIn: "Увійти через Google",
    signOut: "Вийти",
    planLabel: "План",

    // footer
    footerBrand: "Карта емоцій · 2025",
    footerAbout: "Про проєкт",
    footerPrivacy: "Приватність",
    footerFeedback: "Зворотний зв’язок",
    footerSupport: "Підтримати на Ko-fi",

 

    // profile modal
    profileTitle: "Твій профіль",
    profileName: "Ім’я",
    profileEmail: "Email",
    profilePlan: "План",
    profileMoodEntries: "Записів настрою",
    profileLastEntry: "Останній запис",

    yourMoodMap: "Твоя карта настрою",
    subtitle: "Особиста історія емодзі за останні роки.",
    timeRange: "Період",
    range1y: "1 рік",
    range3y: "3 роки",
    rangeAll: "Завжди",
    totalEntries: "Усього записів",
    avgMood: "Середній настрій",
    dominantMood: "Домінуючий настрій",
    tip:
      "Порада: поєднуй цю карту з глобальною аналітикою, щоб бачити, як твій настрій реагує на події у світі.",
  },
};



// ============================
// CONSTS
// ============================

const EMOJI_OPTIONS = [
  { value: "😁", score: 2 },
  { value: "😊", score: 1.5 },
  { value: "😍", score: 1 },
  { value: "😐", score: 0 },
  { value: "😡", score: -0.5 },
  { value: "😢", score: -1.5 },
];

const CONTEXT_OPTIONS = [
  { value: "home", icon: "🏠", label: "Home" },
  { value: "work", icon: "💼", label: "Work" },
  { value: "restaurant", icon: "🍽️", label: "Restaurant" },
  { value: "fun", icon: "🎉", label: "Fun" },
  { value: "shopping", icon: "🛍️", label: "Shopping" },
  { value: "hospital", icon: "🏥", label: "Hospital" },
];

const MOODS_COLLECTION = "moods_v2";

function getScoreForEmoji(emoji) {
  const found = EMOJI_OPTIONS.find((e) => e.value === emoji);
  return found ? found.score : 0;
}

async function fetchPlaceForCoords({ lat, lng }, mapboxToken) {
  if (!mapboxToken || lat == null || lng == null) {
    return { country: null, city: null };
  }

  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxToken}&types=place,locality,region,country&language=en`;
    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error("Geocoding failed");
    }
    const data = await resp.json();

    let country = null;
    let city = null;

    if (Array.isArray(data.features)) {
      for (const f of data.features) {
        if (!Array.isArray(f.place_type)) continue;

        // місто / населений пункт
        if (
          !city &&
          (f.place_type.includes("place") || f.place_type.includes("locality"))
        ) {
          city = f.text;
        }

        // країна
        if (!country && f.place_type.includes("country")) {
          country = f.text;
        }
      }
    }

    return { country, city };
  } catch (e) {
    console.warn("fetchPlaceForCoords error", e);
    return { country: null, city: null };
  }
}

// Дістаємо Date з createdAt (Timestamp / Date)
function getCreatedAtDate(mood) {
  if (!mood || !mood.createdAt) return null;
  const created = mood.createdAt;
  if (typeof created.toDate === "function") {
    return created.toDate();
  }
  if (created instanceof Date) {
    return created;
  }
  return null;
}

// ============================
// MAIN COMPONENT
// ============================

export default function WorldMoodMap() {
  // вибираємо мову: якщо браузер український — "uk", інакше "en"
  const [language, setLanguage] = useState(() => {
    if (typeof navigator !== "undefined") {
      const lang = navigator.language || navigator.userLanguage;
      if (lang && lang.toLowerCase().startsWith("uk")) {
        return "uk";
      }
    }
    return "en";
  });

  const t = translations[language] || translations.en;
  const [activeTab, setActiveTab] = useState("map"); // "map" | "analytics" | "me"
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [mapMode, setMapMode] = useState("emoji"); // "emoji" | "heatmap"
  const [darkTheme, setDarkTheme] = useState(true);

  // стартова позиція карти (НЕ в стейті, щоб не ловити цикли)
  const initialViewState = {
    longitude: 30.52,
    latitude: 50.45,
    zoom: 3,
  };

  // Визначаємо мобільний режим ОДИН раз при першому рендері
  const [isMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  // ref на Mapbox-карту
  const mapRef = useRef(null);

  // Після монтування примусово оновлюємо розмір карти і слухаємо resize вікна
  useEffect(() => {
    if (!mapRef.current) return;

    const handleResize = () => {
      if (mapRef.current && typeof mapRef.current.resize === "function") {
        mapRef.current.resize();
      }
    };

    // одразу після монтування
    handleResize();

    // і при зміні розміру вікна / орієнтації
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);


   const [userLocation, setUserLocation] = useState(null);
  const [userCountry, setUserCountry] = useState(null);
  const [isBlockedCountry, setIsBlockedCountry] = useState(false);
  const [moods, setMoods] = useState([]);

  const [selectedEmoji, setSelectedEmoji] = useState("😊");
  const [selectedContext, setSelectedContext] = useState("home");
  const [note, setNote] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

    // onboarding steps state
  const [hasDoneStepMood, setHasDoneStepMood] = useState(false);
  const [hasDoneStepLocation, setHasDoneStepLocation] = useState(false);
  const [hasDoneStepShare, setHasDoneStepShare] = useState(false);
  // чи вже ховали онбординг для цього користувача
  // чи вже ховали онбординг для цього користувача
const [onboardingHidden, setOnboardingHidden] = useState(() => {
  if (typeof window === "undefined") return false;
  try {
    const done = window.localStorage.getItem("wommapOnboardingDone");
    return done === "true";
  } catch (e) {
    return false;
  }
});




  const [popupMood, setPopupMood] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const [billingPlan, setBillingPlan] = useState("pro");
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);

  // About / Privacy modals
  const [aboutOpen, setAboutOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [blogOpen, setBlogOpen] = useState(false);

   // User profile modal
  const [profileOpen, setProfileOpen] = useState(false);

  // Moods створені саме цим користувачем
  const userMoods = useMemo(() => {
    if (!currentUser || !Array.isArray(moods)) return [];
    return moods.filter((m) => m.userId === currentUser.uid);
  }, [moods, currentUser]);

  // Прості статистики по юзеру
  const userStats = useMemo(() => {
    if (!userMoods.length) return null;

    let total = userMoods.length;
    let sumScore = 0;
    let lastDate = null;

    for (const mood of userMoods) {
      if (typeof mood.score === "number") {
        sumScore += mood.score;
      }

      const created =
        mood.createdAt instanceof Timestamp
          ? mood.createdAt.toDate()
          : mood.createdAt && typeof mood.createdAt.toDate === "function"
          ? mood.createdAt.toDate()
          : null;

      if (created && (!lastDate || created > lastDate)) {
        lastDate = created;
      }
    }

    return {
      totalEntries: total,
      avgScore: total ? sumScore / total : 0,
      lastEntry: lastDate,
    };
  }, [userMoods]);


  // ✅ тільки сьогоднішні емоції для мапи
  const todaysMoods = useMemo(() => {
    if (!Array.isArray(moods) || moods.length === 0) return [];

    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();

    return moods.filter((entry) => {
      const created = getCreatedAtDate(entry);
      if (!created) return false;
      return (
        created.getFullYear() === y &&
        created.getMonth() === m &&
        created.getDate() === d
      );
    });
  }, [moods]);

  // AUTH
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    return () => unsub();
  }, []);

  // FETCH MOODS
  useEffect(() => {
    const qRef = query(
      collection(db, MOODS_COLLECTION),
      orderBy("createdAt", "desc"),
      limit(1000)
    );
    const unsub = onSnapshot(qRef, (snapshot) => {
      const docs = [];
      snapshot.forEach((doc) => {
        docs.push({ id: doc.id, ...doc.data() });
      });
      setMoods(docs);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
  // читаємо з localStorage, чи користувач уже проходив онбординг
  if (typeof window === "undefined") return;

  try {
    const done = window.localStorage.getItem("wommapOnboardingDone");
    if (done === "true") {
      setOnboardingHidden(true);
      setHasDoneStepMood(true);
      setHasDoneStepLocation(true);
      setHasDoneStepShare(true);
    }
  } catch (e) {
    // якщо localStorage недоступний — просто ігноруємо
  }
}, []);

useEffect(() => {
  if (
    hasDoneStepMood &&
    hasDoneStepLocation &&
    hasDoneStepShare &&
    !onboardingHidden
  ) {
    setOnboardingHidden(true);
    try {
      window.localStorage.setItem("wommapOnboardingDone", "true");
    } catch (e) {
      // нічого страшного, якщо не вдалось записати
    }
  }
}, [
  hasDoneStepMood,
  hasDoneStepLocation,
  hasDoneStepShare,
  onboardingHidden,
]);



  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;

    // GET USER LOCATION + detect country
  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const loc = { lat: latitude, lng: longitude };

        setUserLocation(loc);
        setHasDoneStepLocation(true);


// Акуратно центруємо карту на користувачі через mapRef
if (mapRef.current) {
  mapRef.current.flyTo({
    center: [longitude, latitude],
    zoom: 4,
    essential: true,
  });
}

        // спробуємо визначити країну через Mapbox
        try {
          const place = await fetchPlaceForCoords(loc, mapboxToken);
          if (place && place.country) {
            setUserCountry(place.country);

            const c = place.country.toLowerCase();
            if (c === "russia" || c === "russian federation") {
              setIsBlockedCountry(true);
            }
          }
        } catch (e) {
          console.warn("Failed to resolve place for user location", e);
        }
      },
      (err) => {
        console.warn("Geolocation error", err);
      }
    );
  }, [mapboxToken]);


  

  const handleShareMood = async () => {
  // Єдина вимога — геолокація
  if (!userLocation) {
    alert("Unable to get your location. Please allow geolocation.");
    return;
  }

  const selectedConfig =
    EMOJI_OPTIONS.find((e) => e.value === selectedEmoji) || EMOJI_OPTIONS[1];

  // 1) спробувати отримати країну та місто з Mapbox
  let country = null;
  let city = null;
  try {
    const place = await fetchPlaceForCoords(userLocation, mapboxToken);
    country = place.country;
    city = place.city;
  } catch (e) {
    console.warn("Failed to resolve place", e);
  }

  // 2) зберегти все в Firestore
  addDoc(collection(db, MOODS_COLLECTION), {
    emoji: selectedEmoji,
    context: selectedContext,
    note: note || "",
    lat: userLocation.lat,
    lng: userLocation.lng,
    score:
      typeof selectedConfig.score === "number"
        ? selectedConfig.score
        : getScoreForEmoji(selectedEmoji),
    country: country || null,
    city: city || null,
    userId: currentUser ? currentUser.uid : null,
    userDisplayName: currentUser ? currentUser.displayName || "" : "",
    createdAt: serverTimestamp(),
  })
    .then(() => {
    setHasDoneStepShare(true);
    setNote("");
    setSuccessMessage(t.shareThanks || "Mood saved! Thank you for sharing 💛");
    setTimeout(() => setSuccessMessage(""), 5000);
  })
    .catch((e) => {
      console.error("Save mood error", e);
      alert(
        "Error saving mood: " +
          (e.code || "") +
          (e.message ? " – " + e.message : "")
      );
    });
};

  const handleShareTo = (platform) => {
    const url =
      typeof window !== "undefined"
        ? window.location.origin || window.location.href
        : "";

    const baseText =
      language === "uk"
        ? "Мій настрій зараз на WomMap:"
        : "My mood right now on WomMap:";

    const text = `${baseText} ${selectedEmoji || "🙂"}${
      note ? " — " + note : ""
    }`;

    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);

    if (platform === "facebook") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }

    if (platform === "x") {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }

    // Instagram / TikTok: native share або копіювання лінка
    const shareString = `${text} ${url}`;

    if (typeof navigator !== "undefined" && navigator.share) {
      navigator
        .share({
          title: language === "uk" ? "Моя карта настрою" : "My mood map",
          text,
          url,
        })
        .catch(() => {});
    } else if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      navigator.clipboard.writeText
    ) {
      navigator.clipboard
        .writeText(shareString)
        .then(() => {
          alert(
            language === "uk"
              ? "Посилання скопійовано. Встав його в Instagram або TikTok."
              : "Link copied. Paste it into Instagram or TikTok."
          );
        })
        .catch(() => {
          alert(shareString);
        });
    } else {
      alert(shareString);
    }
  };


    const handleSignIn = async () => {
    // якщо країна заблокована — навіть не пробуємо логінитись
    if (isBlockedCountry) {
      alert("На жаль, цей прототип недоступний з вашого регіону.");
      return;
    }

    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      console.error("Sign-in error", e);
    }
  };


  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Sign-out error", e);
    }
  };

  const heatmapData = useMemo(() => {
    if (!todaysMoods || todaysMoods.length === 0) return null;
    return {
      type: "FeatureCollection",
      features: todaysMoods
        .filter((m) => m.lat && m.lng)
        .map((m) => ({
          type: "Feature",
          properties: {
            weight: (m.score ?? getScoreForEmoji(m.emoji || m.moodEmoji)) + 2,
          },
          geometry: {
            type: "Point",
            coordinates: [m.lng, m.lat],
          },
        })),
    };
  }, [todaysMoods]);

  const emojiMarkerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    transform: "translateY(-8px)",
    // трохи підсвічування, щоб смайл не губився на карті
    textShadow: "0 0 6px rgba(15,23,42,0.8)",
  };

  // ============================
    // RENDER
  // ============================

    return (
    <div
      className="wm-root"
      style={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        background: tokens.color.bgApp,
        color: tokens.color.textMain,
      }}
    >
      {/* LEFT SIDE: SIDEBAR */}
    {!(isMobile && activeTab === "me" && currentUser) && (

      <div
        className="wm-sidebar"
        style={{
          width: 360,
          maxWidth: "100%",
          borderRight: `1px solid ${tokens.color.borderSubtle}`,
          overflowY: "auto",
        }}
      >
        <WomCard padding="lg" elevated style={{ minHeight: "100%" }}>

          {/* Header with tabs */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {/* Tabs: Map / Analytics */}
              <WomSegmented
                value={activeTab}
                onChange={setActiveTab}
                style={{ fontSize: 11, whiteSpace: "nowrap" }}
                options={[
                  { value: "map", label: t.tabMap },
                  { value: "analytics", label: t.tabAnalytics },
                  { value: "me", label: t.tabMyMood },
                ]}
              />
            </div>

            {/* Language switcher */}
            <div>
              <WomSegmented
                value={language}
                onChange={setLanguage}
                options={[
                  { value: "uk", label: "UA" },
                  { value: "en", label: "EN" },
                ]}
              />
            </div>
          </div>


          {/* TABS CONTENT */}
          {activeTab === "map" && (
  <>
    {/* Onboarding intro (показувати, поки є хоч один невиконаний крок) */}
    {!onboardingHidden &&
(!hasDoneStepMood || !hasDoneStepLocation || !hasDoneStepShare) ? (
      <div
        style={{
          marginBottom: 8,
          padding: 12,
          borderRadius: 16,
          border: `1px solid ${tokens.color.borderSubtle}`,
          background: "rgba(15,23,42,0.9)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {t.worldMoodTitle}
        </div>
        <div
          style={{
            fontSize: 12,
            color: tokens.color.textSecondary,
          }}
        >
          {t.worldMoodSubtitle}
        </div>

        <div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 6,
    fontSize: 11,
  }}
>
  {/* Step 1 */}
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      opacity: hasDoneStepMood ? 0.6 : 1,
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 999,
        border: hasDoneStepMood
          ? "1px solid rgba(34,197,94,0.9)" // зелена рамка, якщо зроблено
          : "1px solid rgba(56,189,248,0.9)", // блакитна, якщо ні
        background: hasDoneStepMood
          ? "rgba(34,197,94,0.15)"
          : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
      }}
    >
      {hasDoneStepMood ? "✓" : "1"}
    </div>
    <div>
      <div
        style={{
          fontWeight: 500,
          textDecoration: hasDoneStepMood ? "line-through" : "none",
        }}
      >
        {t.onboardingStep1Title}
      </div>
      <div
        style={{
          color: tokens.color.textSecondary,
          textDecoration: hasDoneStepMood ? "line-through" : "none",
        }}
      >
        {t.onboardingStep1Text}
      </div>
    </div>
  </div>

  {/* Step 2 */}
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      opacity: hasDoneStepLocation ? 0.6 : 1,
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 999,
        border: hasDoneStepLocation
          ? "1px solid rgba(34,197,94,0.9)"
          : "1px solid rgba(56,189,248,0.9)",
        background: hasDoneStepLocation
          ? "rgba(34,197,94,0.15)"
          : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
      }}
    >
      {hasDoneStepLocation ? "✓" : "2"}
    </div>
    <div>
      <div
        style={{
          fontWeight: 500,
          textDecoration: hasDoneStepLocation ? "line-through" : "none",
        }}
      >
        {t.onboardingStep2Title}
      </div>
      <div
        style={{
          color: tokens.color.textSecondary,
          textDecoration: hasDoneStepLocation ? "line-through" : "none",
        }}
      >
        {t.onboardingStep2Text}
      </div>
    </div>
  </div>

  {/* Step 3 */}
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      opacity: hasDoneStepShare ? 0.6 : 1,
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 999,
        border: hasDoneStepShare
          ? "1px solid rgba(34,197,94,0.9)"
          : "1px solid rgba(56,189,248,0.9)",
        background: hasDoneStepShare
          ? "rgba(34,197,94,0.15)"
          : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
      }}
    >
      {hasDoneStepShare ? "✓" : "3"}
    </div>
    <div>
      <div
        style={{
          fontWeight: 500,
          textDecoration: hasDoneStepShare ? "line-through" : "none",
        }}
      >
        {t.onboardingStep3Title}
      </div>
      <div
        style={{
          color: tokens.color.textSecondary,
          textDecoration: hasDoneStepShare ? "line-through" : "none",
        }}
      >
        {t.onboardingStep3Text}
      </div>
    </div>
  </div>
</div>

      </div>
    ) : null}

    {/* Далі — твій існуючий контент вкладки Map */}
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Map mode + theme */}
              <WomCard
                padding="md"
                elevated
                style={{
                  background: "rgba(15,23,42,0.95)",
                  border: `1px solid ${tokens.color.borderSubtle}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      color: tokens.color.textSecondary,
                    }}
                  >
                    <span></span>
                    <WomSegmented
                      value={mapMode}
                      onChange={setMapMode}
                      size="sm"
                      options={[
    { value: "emoji", label: t.modeEmoji },
    { value: "heatmap", label: t.modeHeatmap },
  ]}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      color: tokens.color.textSecondary,
                    }}
                  >
                    <span></span>
                    <WomSegmented
                      value={darkTheme ? "dark" : "light"}
                      onChange={(v) => setDarkTheme(v === "dark")}
                      size="sm"
                      options={[
    { value: "dark", label: t.themeDark },
    { value: "light", label: t.themeLight },
  ]}
                    />
                  </div>
                </div>
              </WomCard>

              {/* How do you feel */}
              <WomCard
                padding="md"
                elevated
                style={{
                  background: "rgba(15,23,42,0.96)",
                  border: `1px solid ${tokens.color.borderSubtle}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    marginBottom: 6,
                  }}
                >
                  {t.howDoYouFeel}
                </div>

                {/* Emoji picker */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {EMOJI_OPTIONS.map((opt) => {
                    const active = selectedEmoji === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                              onClick={() => {
        setSelectedEmoji(opt.value);
        setHasDoneStepMood(true);
      }}

                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 40,
                          height: 36,
                          borderRadius: 999,
                          border: `1px solid ${
                            active
                              ? tokens.color.accent
                              : tokens.color.borderSubtle
                          }`,
                          background: active
                            ? "rgba(56,189,248,0.15)"
                            : "rgba(15,23,42,0.9)",
                          color: tokens.color.textMain,
                          cursor: "pointer",
                          fontSize: 22,
                          transition:
                            "background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease",
                          boxShadow: active
                            ? "0 0 0 1px rgba(56,189,248,0.4)"
                            : "none",
                          transform: active ? "translateY(-1px)" : "none",
                        }}
                      >
                        {opt.value}
                      </button>
                    );
                  })}
                </div>

                {/* Where are you */}
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      marginBottom: 6,
                    }}
                  >
                    {t.whereAreYou}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginBottom: 8,
                    }}
                  >
                    {CONTEXT_OPTIONS.map((ctx) => {
                      const active = selectedContext === ctx.value;
                      return (
                        <button
                          key={ctx.value}
                          type="button"
                                onClick={() => {
        setSelectedContext(ctx.value);
        setHasDoneStepLocation(true);
      }}

                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 10px",
                            borderRadius: 999,
                            border: active
                              ? `1px solid ${tokens.color.accent}`
                              : `1px solid ${tokens.color.borderSubtle}`,
                            background: active
                              ? "rgba(56,189,248,0.15)"
                              : "rgba(15,23,42,0.9)",
                            color: tokens.color.textMain,
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                        >
                          <span>{ctx.icon}</span>
      <span>
        {{
          home: t.contextHome,
          work: t.contextWork,
          restaurant: t.contextRestaurant,
          fun: t.contextFun,
          shopping: t.contextShopping,
          hospital: t.contextHospital,
        }[ctx.value] || ctx.label}
      </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Note textarea */}
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={40}
                    placeholder={t.moodNotePlaceholder}
                    style={{
                      width: "90%",
                      minHeight: 60,
                      resize: "vertical",
                      borderRadius: 12,
                      border: `1px solid ${tokens.color.borderSubtle}`,
                      background: "rgba(15,23,42,0.95)",
                      color: tokens.color.textMain,
                      padding: 8,
                      fontSize: 12,
                      fontFamily: "inherit",
                      outline: "none",
                    }}
                  />

                  {note.length > 0 && (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        textAlign: "right",
                        color: tokens.color.textSecondary,
                      }}
                    >
                      {note.length}/40
                    </div>
                  )}
                </div>

                {/* Share button */}
                <WomButton
                  type="primary"
                  onClick={handleShareMood}
                  style={{ ...PRIMARY_CTA_BUTTON, marginTop: 4 }}
                >
                  {t.shareMood} {selectedEmoji}
                </WomButton>

                {/* Inline “Thank you!” */}
                {successMessage && (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#22c55e",
                    }}
                  >
                    {successMessage}
                  </div>
                )}

                {/* Social share buttons */}
                <div
                  style={{
                    marginTop: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: tokens.color.textSecondary,
                    }}
                  >
                    {t.shareOnSocials}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleShareTo("facebook")}
                      style={SOCIAL_BUTTON}
                    >
                      Facebook
                    </button>
                    <button
                      type="button"
                      onClick={() => handleShareTo("x")}
                      style={SOCIAL_BUTTON}
                    >
                      X
                    </button>
                    <button
                      type="button"
                      onClick={() => handleShareTo("instagram")}
                      style={SOCIAL_BUTTON}
                    >
                      Instagram
                    </button>
                    <button
                      type="button"
                      onClick={() => handleShareTo("tiktok")}
                      style={SOCIAL_BUTTON}
                    >
                      TikTok
                    </button>
                  </div>
                </div>
              </WomCard>


              {/* Auth buttons + billing */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 16,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {currentUser ? (
                <>
                  <button
                    type="button"
                    onClick={() => setProfileOpen(true)}
                    style={{
                      ...AUTH_BUTTON,
                      width: "auto",
                      padding: "6px 12px",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      background: "rgba(15,23,42,0.9)",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: "#22c55e",
                      }}
                    />
                    <span>
                      {currentUser.displayName ||
                        currentUser.email ||
                        "Signed in"}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSignOut}
                    style={{
                      ...AUTH_BUTTON,
                      width: "auto",
                      padding: "6px 12px",
                      fontSize: 12,
                    }}
                  >
                    {t.signOut}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleSignIn}
                  style={{
                    ...AUTH_BUTTON,
                    width: "auto",
                    padding: "6px 12px",
                    fontSize: 12,
                  }}
                >
                  {t.signIn}
                </button>
              )}
            </div>

            {/* Billing pill */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: 999,
                border: `1px dashed ${tokens.color.borderSubtle}`,
                background: "rgba(15,23,42,0.85)",
                fontSize: 11,
                color: tokens.color.textSecondary,
              }}
              onClick={() => setBillingOpen(true)}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background:
                    billingPlan === "free"
                      ? "#4ade80"
                      : billingPlan === "pro"
                      ? "#38bdf8"
                      : "#eab308",
                }}
              />
              <span>Plan: {billingPlan}</span>
            </div>
          </div>

                {/* Billing pill */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: 999,
                    border: `1px dashed ${tokens.color.borderSubtle}`,
                    background: "rgba(15,23,42,0.85)",
                    fontSize: 11,
                    color: tokens.color.textSecondary,
                  }}
                  onClick={() => setBillingOpen(true)}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background:
                        billingPlan === "free"
                          ? "#4ade80"
                          : billingPlan === "pro"
                          ? "#38bdf8"
                          : "#eab308",
                    }}
                  />
                                    <span>Plan: {billingPlan}</span>
                </div>
              </div>
            </>
          )}

          {activeTab === "analytics" && (

            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 8,
                }}
              >
                {t.tabAnalytics}
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: tokens.color.textSecondary,
                  marginBottom: 12,
                }}
              >
                {t.analyticsHint}
              </div>

              {isMobile ? (
                // 📱 НА СМАРТФОНІ: показуємо аналітику одразу, без кнопки
                <div
                  style={{
                    borderRadius: 16,
                    border: `1px solid ${tokens.color.borderSubtle}`,
                    overflow: "hidden",
                    marginTop: 12,
                  }}
                >
                  <AnalyticsPanel
                    moods={moods}
                    darkTheme={darkTheme}
                    billingPlan={billingPlan}
                    language={language}
                  />
                </div>
              ) : (
                // 💻 НА НОУТІ: показуємо кнопку, яка відкриває модалку
                <div style={{ marginTop: 12 }}>
                  <WomButton
                    type="primary"
                    onClick={() => setIsAnalyticsModalOpen(true)}
                    style={{
                      ...PRIMARY_CTA_BUTTON,
                      width: "auto",
                      padding: "8px 16px",
                      fontSize: 13,
                      justifyContent: "center",
                    }}
                  >
                    {t.openAnalytics}
                  </WomButton>
                </div>
              )}
            </div>
          )}

          {activeTab === "me" && (
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 8,
                }}
              >
                {t.tabMyMood}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: tokens.color.textSecondary,
                }}
              >
                {t.myMoodHint}
              </div>

              {/* Якщо користувач НЕ залогінений */}
              {!currentUser && (
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      fontSize: 12,
                      marginBottom: 8,
                      color: tokens.color.textSecondary,
                    }}
                  >
                    {language === "uk"
                      ? "Увійдіть через Google, щоб побачити свою карту настрою."
                      : "Sign in with Google to see your personal mood map."}
                  </div>
                  <WomButton
                    type="primary"
                    onClick={handleSignIn}
                    style={{
                      ...PRIMARY_CTA_BUTTON,
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    {t.signIn}
                  </WomButton>
                </div>
              )}

              {/* Якщо користувач уже залогінений */}
              {currentUser && (
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 12,
                    color: tokens.color.textSecondary,
                  }}
                >
                  {language === "uk"
                    ? "Твоя карта настрою відкривається у вікні поверх мапи. Натисни ✕, щоб повернутися."
                    : "Your mood map opens in a modal over the world map. Use ✕ to close it and return."}
                </div>
              )}
            </div>
          )}


                        {/* Footer: About / Privacy / Feedback / Support */}
<div
  style={{
    marginTop: 20,
    paddingTop: 10,
    borderTop: `1px solid ${tokens.color.borderSubtle}`,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  }}
>
  <button
    type="button"
    onClick={() => setAboutOpen(true)}
    style={{
      borderRadius: 999,
      border: `1px solid ${tokens.color.borderSubtle}`,
      background: "rgba(15,23,42,0.9)",
      padding: "4px 10px",
      fontSize: 11,
      color: tokens.color.textSecondary,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    }}
  >
    {t.footerAbout}
  </button>

  <button
    type="button"
    onClick={() => setPrivacyOpen(true)}
    style={{
      borderRadius: 999,
      border: `1px solid ${tokens.color.borderSubtle}`,
      background: "rgba(15,23,42,0.9)",
      padding: "4px 10px",
      fontSize: 11,
      color: tokens.color.textSecondary,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    }}
  >
    {t.footerPrivacy}
  </button>


{/* Нова "кнопка" блогу — це лінк на Substack */}
  <a
    href={SUBSTACK_URL}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      borderRadius: 999,
      border: `1px solid ${tokens.color.borderSubtle}`,
      background: "rgba(15,23,42,0.9)",
      padding: "4px 10px",
      fontSize: 11,
      color: tokens.color.textSecondary,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      textDecoration: "none",
    }}
  >
    {language === "uk" ? "Блог" : "Blog"}
  </a>

  <a
  href={TELEGRAM_FEEDBACK_URL}
  target="_blank"
  rel="noopener noreferrer"
  style={FEEDBACK_BUTTON}
>
  <span>💬</span>
  <span>{t.footerFeedback}</span>
</a>


  <a
    href={PAYPAL_DONATE_URL}
    target="_blank"
    rel="noopener noreferrer"
    style={DONATE_BUTTON}
  >
    <span>💛</span>
    <span>{t.footerSupport}</span>
  </a>

  <a
    href={MONOBANK_DONATE_URL}
    target="_blank"
    rel="noopener noreferrer"
    style={DONATE_BUTTON}
  >
    <span>🇺🇦</span>
    <span>{language === "uk" ? "Монобанк (Україна)" : "Monobank (UA)"}</span>
  </a>
</div>


          </WomCard>
        </div>
      )}


      {/* RIGHT SIDE: MAP */}
      <div className="wm-map" style={{ flex: 1, position: "relative" }}>
        <Map
          mapboxAccessToken={mapboxToken}
          initialViewState={initialViewState}
          maxZoom={10} // трохи обмежуємо зум
          mapStyle={
            darkTheme
              ? "mapbox://styles/mapbox/dark-v11"
              : "mapbox://styles/mapbox/light-v11"
          }
          style={{ width: "100%", height: "100%" }}
        >
          {/* HEATMAP тільки по сьогоднішніх емоціях */}
          {mapMode === "heatmap" && heatmapData && (
            <Source id="mood-heat" type="geojson" data={heatmapData}>
              <Layer
                id="mood-heat-layer"
                type="heatmap"
                paint={{
                  "heatmap-radius": 30,
                  "heatmap-intensity": 0.6,
                  "heatmap-opacity": 0.85,
                  "heatmap-color": [
                    "interpolate",
                    ["linear"],
                    ["heatmap-density"],
                    0,
                    "rgba(15,23,42,0)",
                    0.2,
                    "#0ea5e9",
                    0.4,
                    "#22c55e",
                    0.7,
                    "#eab308",
                    1,
                    "#ef4444",
                  ],
                }}
              />
            </Source>
          )}

          {/* MARKERS по сьогоднішніх емодзі */}
          {mapMode === "emoji" &&
            todaysMoods.map((m) => (
              <Marker
                key={m.id}
                longitude={m.lng}
                latitude={m.lat}
                anchor="center"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setPopupMood(m);
                }}
              >
                <div style={emojiMarkerStyle}>
                  {m.emoji || m.moodEmoji || "😊"}
                </div>
              </Marker>
            ))}

          {/* POPUP */}
          {popupMood && (
            <Popup
              anchor="bottom"
              longitude={popupMood.lng}
              latitude={popupMood.lat}
              onClose={() => setPopupMood(null)}
              closeButton={true}
              closeOnClick={false}
              style={{
                maxWidth: 260,
              }}
            >
              <div
                style={{
                  padding: 8,
                  fontSize: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 18,
                    marginBottom: 4,
                  }}
                >
                  {popupMood.emoji || popupMood.moodEmoji || "😊"}
                </div>
                {popupMood.note && (
                  <div
                    style={{
                      marginBottom: 8,
                      color: "#000000",
                    }}
                  >
                    {popupMood.note}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 11,
                    opacity: 0.8,
                    color: "#000000",
                  }}
                >
                  {popupMood.context && (
                    <span>Context: {popupMood.context} · </span>
                  )}
                  {popupMood.createdAt &&
                    typeof popupMood.createdAt.toDate === "function" && (
                      <span>
                        {popupMood.createdAt.toDate().toLocaleString()}
                      </span>
                    )}
                </div>
              </div>
            </Popup>
          )}
        </Map>

        {/* BILLING PANEL */}
        {billingOpen && (
          <div
            onClick={() => setBillingOpen(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.80)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 40,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 360,
                maxWidth: "100%",
                borderRadius: 16,
                padding: 16,
                background: "rgba(15,23,42,0.98)",
                border: "1px solid rgba(148,163,184,0.35)",
                color: "#e5e7eb",
                boxShadow: "0 25px 60px rgba(15,23,42,0.9)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  Plans
                </div>
                <button
                  type="button"
                  onClick={() => setBillingOpen(false)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#e5e7eb",
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Plans list */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  {
                    id: "free",
                    title: "Free",
                    desc: "Basic map + limited analytics.",
                  },
                  {
                    id: "pro",
                    title: "Pro",
                    desc: "Full analytics + personal mood history (this demo).",
                  },
                  {
                    id: "business",
                    title: "Business",
                    desc: "Custom dashboards & exports.",
                  },
                ].map((plan) => {
                  const active = billingPlan === plan.id;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => {
                        setBillingLoading(true);
                        setTimeout(() => {
                          setBillingPlan(plan.id);
                          setBillingLoading(false);
                        }, 600);
                      }}
                      style={{
                        textAlign: "left",
                        borderRadius: 12,
                        padding: 10,
                        border: active
                          ? "1px solid rgba(56,189,248,0.7)"
                          : "1px solid rgba(148,163,184,0.4)",
                        background: active
                          ? "rgba(15,23,42,0.95)"
                          : "rgba(15,23,42,0.9)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            marginBottom: 2,
                          }}
                        >
                          {plan.title}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "rgba(148,163,184,0.9)",
                          }}
                        >
                          {plan.desc}
                        </div>
                      </div>
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          border: "1px solid rgba(148,163,184,0.8)",
                          background: active
                            ? "rgba(56,189,248,0.9)"
                            : "transparent",
                        }}
                      />
                    </button>
                  );
                })}
              </div>

              {billingLoading && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 11,
                    color: "rgba(148,163,184,0.9)",
                  }}
                >
                  Updating plan…
                </div>
              )}
            </div>
          </div>
        )}

                {/* ANALYTICS MODAL OVER MAP */}
        {activeTab === "analytics" && isAnalyticsModalOpen && (
          <div
            className="wm-modal-overlay"
            onClick={() => {
             setIsAnalyticsModalOpen(false);
             setActiveTab("map");
             }}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.70)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 45,
            }}
          >

            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 760,
                maxWidth: "90%",
                maxHeight: "80%",
                borderRadius: 24,
                overflow: "hidden",
                boxShadow: "0 25px 60px rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.5)",
                background: "transparent",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header inside modal */}
              <div
                style={{
                  padding: "10px 14px",
                  background: "rgba(15,23,42,0.98)",
                  borderBottom: "1px solid rgba(148,163,184,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <WomSegmented
          value={activeTab}
          onChange={setActiveTab}
          size="sm"
          style={{ fontSize: 11, whiteSpace: "nowrap" }}
          options={[
            { value: "map", label: t.tabMap },
            { value: "analytics", label: t.tabAnalytics },
            { value: "me", label: t.tabMyMood },
          ]}
        />
                <button
                  type="button"
                  onClick={() => {
                   setIsAnalyticsModalOpen(false);
                   setActiveTab("map");
                    }}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#e5e7eb",
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>

              <div
                style={{
                  flex: 1,
                  minHeight: 360,
                  background: "transparent",
                }}
              >
                <AnalyticsPanel
                  moods={moods}
                  darkTheme={darkTheme}
                  billingPlan={billingPlan} // ⚡ передаємо план у аналітику
                  language={language}
                />
              </div>
            </div>
          </div>
        )}



                {/* MY MOOD MODAL OVER MAP */}
        {activeTab === "me" && currentUser && (
          <div
            className="wm-modal-overlay"
            onClick={() => {
  setIsAnalyticsModalOpen(false);
  setActiveTab("map");
}}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.70)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 46,
            }}
          >

            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 760,
                maxWidth: "90%",
                maxHeight: "80%",
                borderRadius: 24,
                overflow: "hidden",
                boxShadow: "0 25px 60px rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.5)",
                background: "transparent",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header inside modal */}
              <div
                style={{
                  padding: "10px 14px",
                  background: "rgba(15,23,42,0.98)",
                  borderBottom: "1px solid rgba(148,163,184,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <WomSegmented
          value={activeTab}
          onChange={setActiveTab}
          size="sm"
          style={{ fontSize: 11, whiteSpace: "nowrap" }}
          options={[
            { value: "map", label: t.tabMap },
            { value: "analytics", label: t.tabAnalytics },
            { value: "me", label: t.tabMyMood },
          ]}
        />
                <button
                  type="button"
                  onClick={() => {
  setIsAnalyticsModalOpen(false);
  setActiveTab("map");
}}

                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#e5e7eb",
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>

              <div
                style={{
                  flex: 1,
                  minHeight: 360,
                  background: "transparent",
                }}
              >
                <UserMoodLifeMap
                  moods={moods}
                  userId={currentUser.uid}
                  darkTheme={darkTheme}
                  language={language}
                />
              </div>
            </div>
          </div>
        )}

{/* USER PROFILE MODAL */}
        {profileOpen && currentUser && (
          <div
            onClick={() => setProfileOpen(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.80)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 50,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 520,
                maxHeight: "80vh",
                borderRadius: 20,
                overflow: "hidden",
                boxShadow: "0 25px 60px rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.5)",
                background: "rgba(15,23,42,0.98)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid rgba(148,163,184,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#e5e7eb",
                  }}
                >
                  {/* можна потім винести в переклади */}
                  {t.profileTitle}
                </div>
                <button
                  type="button"
                  onClick={() => setProfileOpen(false)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#e5e7eb",
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div
                style={{
                  padding: "14px 16px 18px",
                  fontSize: 13,
                  color: tokens.color.textSecondary,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 0.08,
                    }}
                  >
                    {t.profileName}
                  </div>
                  <div
                    style={{ fontSize: 14, color: tokens.color.textMain }}
                  >
                    {currentUser.displayName || "—"}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 0.08,
                    }}
                  >
                    {t.profileEmail}
                  </div>
                  <div
                    style={{ fontSize: 14, color: tokens.color.textMain }}
                  >
                    {currentUser.email || "—"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: 0.08,
                      }}
                    >
                      {t.planLabel}: {billingPlan}
                    </div>
                    <div
                      style={{ fontSize: 14, color: tokens.color.textMain }}
                    >
                      {billingPlan}
                    </div>
                  </div>

                  {userStats && (
                    <>
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            textTransform: "uppercase",
                            letterSpacing: 0.08,
                          }}
                        >
                          {t.profileMoodEntries}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            color: tokens.color.textMain,
                          }}
                        >
                          {userStats.totalEntries}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            textTransform: "uppercase",
                            letterSpacing: 0.08,
                          }}
                        >
                          {t.profileLastEntry}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            color: tokens.color.textMain,
                          }}
                        >
                          {userStats.lastEntry
                            ? userStats.lastEntry.toLocaleDateString()
                            : "—"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ABOUT MODAL */}
        {aboutOpen && (
          <div
            onClick={() => setAboutOpen(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.80)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 50,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 640,
                maxWidth: "90%",
                maxHeight: "80%",
                borderRadius: 24,
                overflow: "hidden",
                boxShadow: "0 25px 60px rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.5)",
                background: "rgba(15,23,42,0.98)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid rgba(148,163,184,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                 style={{
                 fontSize: 14,
                 fontWeight: 600,
                 color: "#e5e7eb",
                }}
                >
                 {t.aboutTitle}
                </div>

                <button
                  type="button"
                  onClick={() => setAboutOpen(false)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#e5e7eb",
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>

                      {/* ABOUT MODAL CONTENT */}
        {/* Content */}
<div
  style={{
    padding: 14,
    fontSize: 13,
    color: "#e5e7eb",
    overflowY: "auto",
  }}
>
  {language === "uk" ? (
    <>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        Це експериментальна «карта настроїв». Тут можна поділитися тим,
        як ти почуваєшся зараз, і побачити, як почуваються люди в різних країнах.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        Мета проєкту — обережно підштовхнути до самоусвідомлення, показати,
        як емоції розподіляються в просторі й часі, а також як особистий
        настрій реагує на новини та події навколо.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        WomMap — це:
      </p>
      <ul
        style={{
          marginTop: 0,
          paddingLeft: 18,
          marginBottom: 8,
        }}
      >
        <li>візуальний щоденник настроїв;</li>
        <li>інструмент для рефлексії та дослідження;</li>
        <li><strong>не</strong> медичний сервіс і <strong>не</strong> гаряча лінія допомоги.</li>
      </ul>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        Проєкт не надає медичних, психологічних, юридичних чи фінансових
        рекомендацій. Будь-які рішення ти приймаєш самостійно.
      </p>
      <p style={{ marginTop: 0 }}>
        Автори не несуть відповідальності за спроби використати дані або
        інтерфейс мапи в шкідливих цілях. Якщо тобі дуже погано або є думки
        про самопошкодження — обов’язково звертайся до фахівців чи екстрених
        служб у твоїй країні.
      </p>
    </>
  ) : (
    <>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        WomMap is an experimental &quot;world mood map&quot;. You can
        share how you feel right now and see how people in different
        places on the planet feel today.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        The goal of the project is to gently support self-reflection, show
        how emotions are distributed in space and time, and how personal
        mood reacts to news, events and everyday life.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        WomMap is:
      </p>
      <ul
        style={{
          marginTop: 0,
          paddingLeft: 18,
          marginBottom: 8,
        }}
      >
        <li>a visual diary of moods;</li>
        <li>a tool for reflection and exploration;</li>
        <li><strong>not</strong> a medical service and <strong>not</strong> a crisis helpline.</li>
      </ul>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        This project does not provide medical, psychological, legal or
        financial advice. Any decisions you make are your own responsibility.
      </p>
      <p style={{ marginTop: 0 }}>
        The creators are not responsible for attempts to use the data or
        interface of the map for harmful purposes. If you feel very
        unwell or have thoughts of self-harm, please contact professionals
        or local emergency services.
      </p>
    </>
  )}
</div>

      </div>
    </div>
  )}

  {/* PRIVACY MODAL */}
  {privacyOpen && (
    <div
      onClick={() => setPrivacyOpen(false)}
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(15,23,42,0.80)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 51,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 640,
          maxWidth: "90%",
          maxHeight: "80%",
          borderRadius: 24,
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(15,23,42,0.9)",
          border: "1px solid rgba(148,163,184,0.5)",
          background: "rgba(15,23,42,0.98)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "10px 14px",
            borderBottom: "1px solid rgba(148,163,184,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#e5e7eb",
            }}
          >
            Privacy &amp; data
          </div>
          <button
            type="button"
            onClick={() => setPrivacyOpen(false)}
            style={{
              border: "none",
              background: "transparent",
              color: "#e5e7eb",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
<div
  style={{
    padding: 14,
    fontSize: 13,
    color: "#e5e7eb",
    overflowY: "auto",
  }}
>
  {language === "uk" ? (
    <>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        Коли ти ділишся настроєм, ми зберігаємо емодзі, коротку нотатку
        (якщо ти її додаєш), приблизну локацію (країна / місто) та час запису.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        Також можуть зберігатися технічні дані, потрібні для роботи сервісу
        (наприклад, координати точки на мапі та технічний ідентифікатор
        користувача), щоб захиститися від спаму та зловживань.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        Ці дані використовуються для того, щоб:
      </p>
      <ul
        style={{
          marginTop: 0,
          paddingLeft: 18,
          marginBottom: 8,
        }}
      >
        <li>показувати сьогоднішній настрій на мапі;</li>
        <li>будувати агреговану аналітику за країнами та містами;</li>
        <li>показувати тобі історію власних записів, якщо ти в акаунті.</li>
      </ul>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        У сервісі немає рекламних трекерів чи пікселів соцмереж.
        Дані можуть використовуватися в анонімізованій, агрегованій формі
        для досліджень та візуалізацій.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        Ми не продаємо персональні дані. Водночас ми не можемо повністю
        контролювати, як інші люди інтерпретують або використовують
        відкриті візуалізації настроїв.
      </p>
      <p style={{ marginTop: 0 }}>
        Автори не несуть відповідальності за будь-яку шкоду, завдану
        спробами використати цей проєкт або його дані для маніпуляцій,
        дискримінації чи інших шкідливих дій.
      </p>
    </>
  ) : (
    <>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        When you share a mood, we store the emoji you chose, the short note
        (if you add one), approximate location (country / city) and the
        time of the entry.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        We may also store technical data needed to run the service (for example,
        the coordinates of the point on the map and a technical user identifier)
        to protect against spam and abuse.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        We use this data to:
      </p>
      <ul
        style={{
          marginTop: 0,
          paddingLeft: 18,
          marginBottom: 8,
        }}
      >
        <li>show today’s mood on the world map;</li>
        <li>build aggregated analytics by countries and cities;</li>
        <li>render your personal mood history when you are signed in.</li>
      </ul>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        There are no advertising trackers or social media pixels in the service.
        Data may be used in anonymised, aggregated form for research and
        visualisations.
      </p>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        We do not sell personal data. However, we cannot fully control how
        other people interpret or reuse public visualisations of moods.
      </p>
      <p style={{ marginTop: 0 }}>
        The creators are not responsible for any harm caused by attempts to use
        this project or its data for manipulation, discrimination or other
        harmful actions.
      </p>
    </>
  )}
</div>

      </div>
    </div>
  )}
        {/* BLOG MODAL */}
        {blogOpen && (
          <div
            onClick={() => setBlogOpen(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.80)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 52,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 640,
                maxWidth: "90%",
                maxHeight: "80%",
                borderRadius: 24,
                overflow: "hidden",
                boxShadow: "0 25px 60px rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.5)",
                background: "rgba(15,23,42,0.98)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid rgba(148,163,184,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#e5e7eb",
                  }}
                >
                  {language === "uk" ? "Блог WomMap" : "WomMap Blog"}
                </div>
                <button
                  type="button"
                  onClick={() => setBlogOpen(false)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#e5e7eb",
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Content */}
              <div
                style={{
                  padding: 14,
                  fontSize: 13,
                  color: "#e5e7eb",
                  overflowY: "auto",
                }}
              >
                {language === "uk" ? (
                  <>
                    <h3
                      style={{
                        marginTop: 0,
                        marginBottom: 8,
                        fontSize: 14,
                      }}
                    >
                      Чому я створив WomMap
                    </h3>
                    <p style={{ marginTop: 0, marginBottom: 8 }}>
                      WomMap народився як невеликий експеримент. Хотілося
                      створити інструмент, який м’яко запитує: &quot;Як ти
                      насправді почуваєшся сьогодні?&quot; — без довгих анкет і
                      складних дашбордів.
                    </p>
                    <p style={{ marginTop: 0, marginBottom: 8 }}>
                      Емодзі — це простий спосіб позначити стан. На основі цих
                      маленьких позначок з’являється карта настроїв, особиста
                      аналітика та відчуття, що твої емоції теж частина великої
                      картини.
                    </p>
                    <p style={{ marginTop: 0 }}>
                      Далі в блозі я хочу ділитися тим, як розвивається проєкт,
                      які інсайти з’являються з даних та які фічі планую
                      додавати. Якщо тобі цікаво — пиши ідеї через Telegram
                      або форму зворотного звʼязку 💛
                    </p>
                  </>
                ) : (
                  <>
                    <h3
                      style={{
                        marginTop: 0,
                        marginBottom: 8,
                        fontSize: 14,
                      }}
                    >
                      Why I created WomMap
                    </h3>
                    <p style={{ marginTop: 0, marginBottom: 8 }}>
                      WomMap started as a small experiment. I wanted to build a
                      tool that gently asks: &quot;How do you actually feel
                      today?&quot; — without long questionnaires or complicated
                      dashboards.
                    </p>
                    <p style={{ marginTop: 0, marginBottom: 8 }}>
                      Emojis are a simple way to mark a state. Based on these
                      tiny check-ins, a map of moods appears, along with
                      personal analytics and a sense that your emotions are also
                      part of a bigger picture.
                    </p>
                    <p style={{ marginTop: 0 }}>
                      In this blog I plan to share how the project evolves, what
                      insights come from the data, and which new features I want
                      to add. If you&apos;re curious, feel free to send ideas via
                      Telegram or the feedback form 💛
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

